import classNames from 'classnames';
import * as queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LC_SCREENSHOT_FB_WIDGET } from '@mm/api-routes';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { ChatWidgetPreviewIFrameProps } from './ChatWidgetPreviewFrame';
import styles from './ChatWidgetPreviewMessengerScreenshotFrame.module.css';
import { Alert } from '@mm/components';
import {
  PrimaryButton,
  ButtonColor,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';

export const ChatWidgetPreviewMessengerScreenshotFrame = (
  props: ChatWidgetPreviewIFrameProps,
) => {
  const { className, chatWidget, greetingWidget, onGreetingShown } = props;

  const { fbAppId } = useSelector((state: StoreState) => state.env);
  const fbPage = useSelector(activeFacebookPageSelector);
  const userAuthToken = useSelector(
    (state: StoreState) => state.session.session.authToken,
  );

  const [siteImage, setSiteImage] = useState<string>();
  const [siteImageError, setSiteImageError] = useState<
    'nocontent' | 'error' | undefined
  >();

  const containerRef = useRef<HTMLDivElement>(null);

  const loadImage = async (attempt: number = 0): Promise<void> => {
    if (!fbPage) {
      return;
    }
    setSiteImage(undefined);
    setSiteImageError(undefined);
    const greeting = greetingWidget
      ? greetingWidget.text ||
        (greetingWidget.attachment &&
          greetingWidget.attachment.payload &&
          greetingWidget.attachment.payload.text)
      : chatWidget.loggedOutGreeting ||
        "Hi! We're here to answer any questions you may have";
    const params = {
      origin: window.location.origin,
      widget: JSON.stringify({
        fbAppId,
        pageId: fbPage.remoteId,
        greeting,
        ...(chatWidget.color ? { color: chatWidget.color } : {}),
      }),
    };

    try {
      const response = await fetch(
        `${LC_SCREENSHOT_FB_WIDGET}?${queryString.stringify(params)}`,
        {
          method: 'GET',
          headers: {
            'MM-User-Authtoken': userAuthToken,
            'MM-Remote-Page-Id': fbPage.remoteId,
          },
        },
      );

      if (response.status === 200) {
        setSiteImage(URL.createObjectURL(await response.blob()));
      } else if (response.status === 204) {
        setSiteImageError('nocontent');
      } else {
        if (attempt < 1) {
          return loadImage(attempt + 1);
        }
        setSiteImageError('error');
      }
      setTimeout(() => {
        containerRef.current?.scrollTo(0, containerRef.current.scrollHeight);
        onGreetingShown?.();
      }, 10);
    } catch {
      setSiteImageError('error');
    }
  };

  useEffect(() => {
    loadImage();
    return () => {
      setSiteImage(undefined);
    };
  }, [chatWidget, fbAppId, fbPage, greetingWidget]);

  if (siteImageError) {
    return (
      <div className={styles.errorContainer}>
        {(() => {
          switch (siteImageError) {
            case 'nocontent':
              return (
                <Alert type="info" showCloseIcon={false}>
                  <div className={styles.error}>
                    Unable to generate preview
                    <PrimaryButton
                      color={ButtonColor.transparent}
                      onClick={() => loadImage()}
                    >
                      Reload
                    </PrimaryButton>
                  </div>
                  <div className={styles.errorInfo}>
                    You may have some restrictions on your Facebook page that
                    prevent the chat widget from being shown to users not logged
                    into Facebook.
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://customers.ai/help/article/vd1k01n4ex-customer-chat-isn-t-showing-on-my-website"
                    >
                      Learn more here.
                    </a>
                  </div>
                </Alert>
              );

            case 'error':
            default:
              return (
                <Alert type="error" showCloseIcon={false}>
                  <div className={styles.error}>
                    Failed to load preview.
                    <PrimaryButton
                      color={ButtonColor.transparent}
                      onClick={() => loadImage()}
                    >
                      Retry
                    </PrimaryButton>
                  </div>
                </Alert>
              );
          }
        })()}
      </div>
    );
  }
  if (!siteImage) {
    return null;
  }
  return (
    <div ref={containerRef} className={classNames(className, styles.container)}>
      <div className={styles.imageContainer}>
        <img className={styles.image} src={siteImage} />
      </div>
    </div>
  );
};
